import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  HomeIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  RectangleStackIcon,
  KeyIcon,
  ChatBubbleLeftRightIcon,
  CogIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  // Import new icons
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  BellAlertIcon,
  ArrowUpTrayIcon,
  ClipboardDocumentListIcon,
  ShieldExclamationIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon as InterventionIcon
} from '@heroicons/react/24/outline';

interface LayoutProps {
  children: React.ReactNode;
}

// Helper function to capitalize first letter of each word
const capitalizeWords = (name: string | undefined) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load sidebar state from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved ? JSON.parse(saved) : false;
    }
    return false;
  });
  const { user, logout } = useAuth();
  const location = useLocation();
  // Only admins should see the sidebar
  const showSidebar = user?.role === 'admin';

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
    }
  }, [sidebarCollapsed]);

  // Function to get current reporting time
  const getCurrentReportingTime = () => {
    // Get current time in Malawi (CAT - Central Africa Time, UTC+2)
    const now = new Date();
    const malawiTime = new Date(now.toLocaleString("en-US", { timeZone: "Africa/Blantyre" }));
    const hours = malawiTime.getHours();

    if (hours >= 6 && hours < 12) {
      return { label: 'Morning', period: '06:00 - 12:00', color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' };
    } else if (hours >= 12 && hours < 16) {
      return { label: 'Afternoon', period: '12:00 - 16:00', color: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' };
    } else {
      return { label: 'Vote Count', period: '16:00 - 06:00', color: 'text-indigo-600', bgColor: 'bg-indigo-50', borderColor: 'border-indigo-200' };
    }
  };

  const reportingTime = getCurrentReportingTime();
      // { name: 'Locations Import', href: '/admin/locations-import', icon: ArrowUpTrayIcon },
  const getNavigationItems = () => {
    const allNavigation = [
      { name: 'Main Dashboard', href: '/admin/dashboard', icon: ChartBarIcon },
      { name: 'E-Day Dashboard', href: '/admin/eday-dashboard', icon: ClipboardDocumentCheckIcon },
      { name: 'Incident Dashboard', href: '/admin/incidents-dashboard', icon: BellAlertIcon },
      { name: 'Compliance Dashboard', href: '/admin/compliance-dashboard', icon: ClipboardDocumentListIcon },
      { name: 'E-Day Checklist', href: '/admin/reports', icon: DocumentTextIcon },
      { name: 'Incidents Reports', href: '/admin/incidents', icon: ExclamationTriangleIcon },
      { name: 'Incidents Action', href: '/admin/incidents-action', icon: ShieldExclamationIcon },
      { name: 'Hub Actions', href: '/admin/hub-actions', icon: WrenchScrewdriverIcon },
      { name: 'Incidents Intervention', href: '/admin/incidents-intervention', icon: InterventionIcon },

      { name: 'Users', href: '/admin/users', icon: UsersIcon },
      { name: 'Organizations', href: '/admin/organizations', icon: BuildingOfficeIcon },
      // Hidden menu items (commented out)
      // { name: 'Roles', href: '/admin/roles', icon: ShieldCheckIcon },
      // { name: 'Modules', href: '/admin/modules', icon: RectangleStackIcon },
      // { name: 'Permissions', href: '/admin/permissions', icon: KeyIcon },
      // { name: 'SMS Messages', href: '/admin/sms', icon: ChatBubbleLeftRightIcon },
      // { name: 'Settings', href: '/admin/settings', icon: CogIcon },
      // { name: 'Moderator Action', href: '/admin/moderator-action', icon: ShieldExclamationIcon },
    ];

    // If user is observer, only show Main Dashboard
    if (user?.role === 'observer') {
      return [{ name: 'Main Dashboard', href: '/admin/dashboard', icon: ChartBarIcon }];
    }

    // For other roles, show all navigation items
    return allNavigation;
  };

  const navigation = getNavigationItems();

  const isCurrentPath = (href: string) => {
    return location.pathname === href;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar (admin only) */}
      {showSidebar && (
        <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}>
          <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity ease-linear duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setSidebarOpen(false)} />

          <div className={`relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-white transform transition ease-in-out duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
            <div className="absolute top-0 right-0 pt-2 -mr-12">
              <button
                type="button"
                className="flex items-center justify-center w-10 h-10 ml-1 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <XMarkIcon className="w-6 h-6 text-white" />
              </button>
            </div>

            <div className="flex items-center flex-shrink-0 px-4">
              <img src="/logo.png" alt="Maso Athu" className="w-8 h-8 mr-3" />
              <h1 className="text-xl font-bold text-gray-900">MASO ESR</h1>
            </div>

            <div className="flex-1 h-0 mt-5 overflow-y-auto">
              <nav className="px-2 space-y-1">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-base font-medium rounded-md transition-colors duration-200 ${isCurrentPath(item.href)
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon
                      className={`mr-4 flex-shrink-0 h-6 w-6 transition-colors duration-200 ${isCurrentPath(item.href) ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                        }`}
                    />
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar (admin only) */}
      {showSidebar && (
        <div className={`hidden md:flex md:flex-col md:fixed md:inset-y-0 transition-all duration-300 ${sidebarCollapsed ? 'md:w-16' : 'md:w-64'
          }`}>
          <div className="flex flex-col flex-grow pt-5 overflow-y-auto bg-white border-r border-gray-200">
            {/* Header */}
            <div className="flex items-center flex-shrink-0 px-4 mb-4">
              <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : ''}`}>
                <img src="/logo.png" alt="Maso Athu" className="flex-shrink-0 w-8 h-8" />
                {!sidebarCollapsed && (
                  <h1 className="ml-3 text-xl font-bold text-gray-900 whitespace-nowrap">MASO ESR Admin</h1>
                )}
              </div>
            </div>

            <div className="flex flex-col flex-grow mt-1">
              <nav className="flex-1 px-2 pb-4 space-y-1">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-all duration-200 ${isCurrentPath(item.href)
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      } ${sidebarCollapsed ? 'justify-center' : ''}`}
                    title={sidebarCollapsed ? item.name : ''}
                  >
                    <item.icon
                      className={`flex-shrink-0 h-5 w-5 transition-colors duration-200 ${isCurrentPath(item.href) ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                        } ${sidebarCollapsed ? '' : 'mr-3'}`}
                    />
                    {!sidebarCollapsed && (
                      <span className="whitespace-nowrap">{item.name}</span>
                    )}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className={`flex flex-col flex-1 transition-all duration-300 ${showSidebar ? (sidebarCollapsed ? 'md:pl-16' : 'md:pl-64') : ''
        }`}>
        {/* Top bar */}
        <div className="sticky top-0 z-50 flex flex-shrink-0 h-16 bg-white shadow">
          {showSidebar && (
            <button
              type="button"
              className="px-4 text-gray-500 border-r border-gray-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
          )}

          {/* Desktop sidebar toggle button */}
          <button
            type="button"
            className="hidden px-4 text-gray-500 transition-colors duration-200 border-r border-gray-200 md:flex focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 hover:text-gray-700 hover:bg-gray-50"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? (
              <Bars3Icon className="w-6 h-6" />
            ) : (
              <XMarkIcon className="w-6 h-6" />
            )}
          </button>

          <div className="flex items-center justify-between flex-1 px-2 sm:px-4">
            {/* Reporting Time Indicator - Hidden on small screens */}
            <div className={`hidden sm:flex items-center space-x-2 px-3 py-1.5 rounded-lg border ${reportingTime.bgColor} ${reportingTime.borderColor} animate-pulse`}>
              <div className="relative">
                <div className={`h-2 w-2 rounded-full ${reportingTime.color.replace('text-', 'bg-')} animate-ping absolute`}></div>
                <div className={`h-2 w-2 rounded-full ${reportingTime.color.replace('text-', 'bg-')}`}></div>
              </div>
              <div>
                <div className={`text-sm font-semibold ${reportingTime.color} animate-pulse`}>
                  Reporting Time: {reportingTime.label}
                </div>
                <div className="text-xs text-gray-500">
                  {reportingTime.period}
                </div>
              </div>
            </div>

            {/* Mobile - Just show current time period */}
            <div className={`sm:hidden flex items-center space-x-1 px-2 py-1 rounded border ${reportingTime.bgColor} ${reportingTime.borderColor}`}>
              <div className={`h-2 w-2 rounded-full ${reportingTime.color.replace('text-', 'bg-')}`}></div>
              <span className={`text-xs font-semibold ${reportingTime.color}`}>
                {reportingTime.label}
              </span>
            </div>

            <div className="flex items-center ml-2 sm:ml-4">
              <div className="relative">
                <div className="flex items-center space-x-1 sm:space-x-3">
                  {/* Desktop - show name and role */}
                  <span className="hidden text-sm font-medium text-gray-700 sm:block">{capitalizeWords(user?.name)}</span>
                  <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    {user?.role}
                  </span>
                  <button
                    onClick={logout}
                    className="p-1 text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    title="Logout"
                  >
                    <ArrowRightOnRectangleIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="px-4 mx-auto sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
